import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useI18n } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import Seo from "@/components/Seo";
import { Kicker } from "@/components/Primitives";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { LanguageSwitcher } from "@/components/PublicLayout";
import PortalDocuments from "@/pages/portal/PortalDocuments";

const EDITABLE = ["first_name", "last_name", "phone", "city", "province_state", "country", "career_goals", "motivation", "availability"];

export default function Portal() {
  const { t, lang } = useI18n();
  const { user, logout } = useAuth();
  const [data, setData] = useState(null);
  const [status, setStatus] = useState(null);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [me, st] = await Promise.all([api.get("/portal/me"), api.get("/portal/status")]);
      setData(me.data);
      setStatus(st.data);
      const profile = me.data.profile || {};
      setForm({
        ...EDITABLE.reduce((acc, key) => ({ ...acc, [key]: profile[key] || "" }), {}),
        marketing_consent: Boolean(profile.marketing_consent),
      });
    } catch (error) {
      toast.error(apiError(error, t("common.error")));
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async (event) => {
    event.preventDefault();
    setBusy(true);
    try {
      await api.put("/portal/profile", form);
      toast.success(t("portal.updated"));
      load();
    } catch (error) {
      toast.error(apiError(error, t("common.error")));
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    try {
      await api.post("/auth/resend-verification");
      toast.success(t("auth.resent"));
    } catch (error) {
      toast.error(apiError(error, t("common.error")));
    }
  };

  const stage = status?.stage;

  return (
    <div data-testid="portal-page" className="min-h-screen bg-background">
      <Seo title={t("portal.title")} description={t("portal.title")} path="/portal" noindex />
      <header className="border-b border-border bg-surface">
        <div className="cs-container flex h-16 items-center justify-between gap-4">
          <Link to={`/${lang}`} className="font-display text-base font-semibold tracking-tight sm:text-lg">
            CONSULTANTS SPARKS
          </Link>
          <div className="flex items-center gap-4">
            <LanguageSwitcher testId="portal-language-switcher" />
            <button data-testid="portal-logout" onClick={logout} className="text-sm text-muted-foreground hover:text-foreground">
              {t("cta.logout")}
            </button>
          </div>
        </div>
      </header>

      <div className="cs-container py-12">
        <Kicker>{t("portal.title")}</Kicker>
        <h1 data-testid="portal-welcome" className="mt-4 font-display text-3xl font-light sm:text-4xl">
          {t("portal.welcome")}, {user?.first_name}
        </h1>

        {user && !user.email_verified && (
          <div data-testid="portal-unverified" className="mt-8 flex flex-wrap items-center gap-4 border border-accent/40 bg-accent/5 p-4 text-sm">
            <span>{t("auth.unverified")}</span>
            <button onClick={resend} data-testid="portal-resend-verification" className="text-accent underline">
              {t("auth.resend")}
            </button>
          </div>
        )}

        <div className="mt-10 grid grid-cols-1 gap-8 lg:grid-cols-12">          <section data-testid="portal-status" className="lg:col-span-5 border border-border bg-surface p-6">
            <h2 className="font-display text-2xl">{t("portal.statusTitle")}</h2>
            {status && !status.has_application ? (
              <div className="mt-6">
                <p className="text-sm text-muted-foreground">{t("portal.noApplication")}</p>
                <Button asChild className="mt-6 rounded-full bg-accent text-accent-foreground hover:bg-accent/90">
                  <Link to={`/${lang}/apply`} data-testid="portal-apply-link">
                    {t("portal.applyNow")}
                  </Link>
                </Button>
              </div>
            ) : (
              <div className="mt-6 space-y-6">
                <div>
                  <p className="cs-kicker">{t("portal.currentStage")}</p>
                  <p data-testid="portal-current-stage" className="mt-2 font-display text-2xl text-accent">
                    {stage ? t(`stages.${stage}`) : "—"}
                  </p>
                </div>
                <div>
                  <p className="cs-kicker">{t("portal.nextStepTitle")}</p>
                  <p data-testid="portal-next-step" className="mt-2 text-sm text-muted-foreground">
                    {stage ? t(`portal.nextStep.${stage}`) : "—"}
                  </p>
                </div>
                <div>
                  <p className="cs-kicker">{t("portal.historyTitle")}</p>
                  <ol data-testid="portal-history" className="mt-3 divide-y divide-border border-y border-border">
                    {(status?.history || []).map((entry) => (
                      <li key={entry.id} className="flex items-center justify-between gap-4 py-3 text-sm">
                        <span>{t(`stages.${entry.to_stage}`)}</span>
                        <span className="text-xs text-muted-foreground">{(entry.created_at || "").slice(0, 10)}</span>
                      </li>
                    ))}
                  </ol>
                </div>
              </div>
            )}
          </section>

          <section className="lg:col-span-7 border border-border bg-surface p-6">
            <h2 className="font-display text-2xl">{t("portal.profileTitle")}</h2>
            <form onSubmit={save} data-testid="portal-profile-form" className="mt-6 space-y-5">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                {["first_name", "last_name", "phone", "city", "province_state", "country"].map((key) => (
                  <div key={key}>
                    <Label htmlFor={`portal-${key}`}>{t(`apply.fields.${key === "province_state" ? "provinceState" : key === "first_name" ? "firstName" : key === "last_name" ? "lastName" : key}`)}</Label>
                    <Input
                      id={`portal-${key}`}
                      data-testid={`portal-${key.replace(/_/g, "-")}`}
                      value={form[key] || ""}
                      onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                      className="mt-2"
                    />
                  </div>
                ))}
              </div>
              <div>
                <Label htmlFor="portal-career-goals">{t("apply.fields.careerGoals")}</Label>
                <Textarea id="portal-career-goals" data-testid="portal-career-goals" rows={3} value={form.career_goals || ""}
                          onChange={(e) => setForm({ ...form, career_goals: e.target.value })} className="mt-2" />
              </div>
              <label className="flex items-start gap-3 text-sm">
                <Checkbox data-testid="portal-marketing-consent" checked={Boolean(form.marketing_consent)}
                          onCheckedChange={(v) => setForm({ ...form, marketing_consent: Boolean(v) })} />
                <span>{t("apply.consent.marketing")}</span>
              </label>
              <div className="flex flex-wrap items-center gap-4">
                <Button type="submit" disabled={busy} data-testid="portal-save" className="rounded-full bg-accent px-8 text-accent-foreground hover:bg-accent/90">
                  {busy ? t("common.saving") : t("common.save")}
                </Button>
                <span className="text-xs text-muted-foreground">{data?.account?.email}</span>
              </div>
            </form>
          </section>
        </div>

        <div className="mt-8">
          <PortalDocuments />
        </div>
      </div>
    </div>
  );
}
